/**
 * PortalDropdown
 * Shared anchored-portal dropdown/action-menu, replacing both:
 *  - CardActionsPopover's IonPopover (shadow-DOM ::part(content) doesn't
 *    reliably take a custom background in this app), and
 *  - the near-identical PortalDropdown/usePortalDropdownPosition (and
 *    HelcimPlansModal's RowMenu/useRowMenuPosition) locally duplicated in
 *    HelcimSubscriptionsModal.tsx / HelcimPlansModal.tsx.
 * Portals an IonCard into an in-page `portalRef` container (a fixed,
 * inset:0, pointer-events:none div) positioned via getBoundingClientRect(),
 * with an outside-pointerdown-to-close backdrop.
 *
 * Calling convention: standardized on per-option `handler` (falling back to
 * a shared `onSelect(value)` when an option has no handler of its own) —
 * this is the least invasive shape across all 8 former call sites: it's a
 * strict superset of CardActionsPopover's per-action `handler` (6 call
 * sites, unchanged) while the 2 Helcim call sites' `value` + shared
 * `onSelect` convention still works unmodified by simply continuing to pass
 * `onSelect` and per-option `value`. `selected` (CardActionsPopover) and
 * Helcim's `opt.value === value` active-check are unified into a single
 * `selected?: boolean` field — call sites that used to rely on `value`
 * matching now compute `selected` themselves.
 *
 * `role: 'destructive'` (CardActionsPopover) and Helcim's `destructive`
 * boolean are unified as `role?: 'destructive'`.
 *
 * `onOpenChange` (CardActionsPopover) had exactly one real call site
 * (Calendar.tsx, driving a trigger button's active-state class) and that
 * state is now the same explicit `isOpen` the caller already owns for this
 * component — so `onOpenChange` is dropped; callers derive it from their
 * own `isOpen` state directly.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IonCard, IonList, IonItem, IonIcon, IonLabel, IonText, IonCheckbox } from '@ionic/react';
import styles from './PortalDropdown.module.scss';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';

export interface PortalDropdownOption {
  label: string;
  value?: string;
  sublabel?: string;
  icon?: string | React.ReactNode;
  endIcon?: string;
  color?: string;
  role?: 'destructive';
  selected?: boolean;
  /** Per-option click handler. Falls back to the shared `onSelect(value)` when absent. */
  handler?: () => void;
}

type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number; maxHeight?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  align: 'start' | 'end' = 'start',
  // 'start' normally also matches the dropdown's width to the trigger's own
  // width. Some callers' content is wider than the trigger, so matchWidth
  // lets the card size itself via width:fit-content/max-width instead.
  matchWidth = true,
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Re-runs the positioning effect when the keyboard opens/closes (e.g. tapping the dropdown's search).
  const keyboardHeight = useKeyboardHeight();
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const width = align === 'start' && matchWidth ? box.width : undefined;

      // Flip upward when there isn't enough room below the anchor but there
      // is more above it — matches Contact.tsx's/usePortalPickerRect's flip
      // logic, needed for triggers that sit near the bottom of the screen
      // (e.g. a footer action button) where opening downward would clip the
      // dropdown against the viewport edge. Only trusts the card's own
      // measured height; before that's known (first paint) it opens
      // downward, then the rAF recompute below corrects it.
      const tabBar = globalThis.document.querySelector('ion-tab-bar');
      const tabBarHeight = tabBar ? tabBar.getBoundingClientRect().height : 0;
      // The native app runs the keyboard in resize:none mode (see useKeyboardHeight), so
      // window.innerHeight still includes the area under the keyboard — treat the keyboard's top
      // edge as the bottom of the usable screen, or a dropdown opens "below" behind it.
      const spaceBelow = window.innerHeight - keyboardHeight - tabBarHeight - box.bottom;
      const spaceAbove = box.top;
      // Natural (uncapped) content height, NOT getBoundingClientRect().height: that already
      // reflects the inline maxHeight set below, so measuring it shrank the card to fit under the
      // anchor and the flip test could never pass — the list ran off the bottom. scrollHeight is
      // independent of maxHeight, so the decision is stable (same intent as Contact.tsx's version).
      // Natural height with our own inline maxHeight cleared (restored right after, same frame, so no
      // visible change). scrollHeight under-reports for cards whose children shrink inside a capped
      // flex column (the phone country list), and getBoundingClientRect() alone reports the height
      // already shrunk to fit below the anchor — either way the card could never be seen to not fit.
      const card = cardRef.current;
      let cardHeight = 0;
      if (card) {
        const prevMaxHeight = card.style.maxHeight;
        card.style.maxHeight = '';
        cardHeight = card.getBoundingClientRect().height;
        card.style.maxHeight = prevMaxHeight;
      }
      const flipUp = cardHeight > 0 && spaceBelow < cardHeight + 4 && spaceAbove > spaceBelow;

      const available = (flipUp ? spaceAbove : spaceBelow) - 12;
      let maxHeight = Math.max(96, Math.min(320, available));
      let top = flipUp
        ? box.top - anchor.top - Math.min(cardHeight, maxHeight) - 4
        : box.bottom - anchor.top + 4;

      // Hard guarantee while the keyboard is up (it doesn't resize the screen in the native app): the
      // whole card — including a search box at its top — must end above the keyboard's top edge, even
      // when it fits neither cleanly below nor above its anchor. Slide it up over the anchor if
      // needed, and shrink it only if it still can't fit between the top of the screen and the keyboard.
      if (keyboardHeight > 0) {
        const limit = window.innerHeight - keyboardHeight - 8;
        const minAbsTop = 56; // stay clear of the status bar / header
        const height = Math.min(cardHeight || maxHeight, maxHeight);
        if (anchor.top + top + height > limit) {
          const newAbsTop = Math.max(minAbsTop, limit - height);
          top = newAbsTop - anchor.top;
          maxHeight = Math.max(96, Math.min(maxHeight, limit - newAbsTop));
        }
      }

      if (align === 'end') {
        return { top, right: anchor.right - box.right, maxHeight };
      }
      return { top, left: box.left - anchor.left, width, maxHeight };
    };
    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
      card.style.maxHeight = r.maxHeight !== undefined ? `${r.maxHeight}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    // Recompute once the card's real height is measurable / whenever it
    // changes — needed for the flip-up decision above, which depends on
    // cardHeight and would otherwise stay wrong for the rest of this open
    // if the two rAFs above land before layout has settled (e.g. a
    // longer option list that takes a frame longer to size).
    const resizeObserver = new ResizeObserver(() => {
      const r = computeRect();
      if (r) { setRect(r); applyRect(r); }
    });
    let attachRaf = 0;
    const tryAttachObserver = () => {
      if (cardRef.current) {
        resizeObserver.observe(cardRef.current);
        return;
      }
      attachRaf = requestAnimationFrame(tryAttachObserver);
    };
    tryAttachObserver();

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(attachRaf);
      resizeObserver.disconnect();
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, align, matchWidth, keyboardHeight]);

  return rect;
}

interface Props {
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  options: PortalDropdownOption[];
  title?: string;
  /** Shared fallback invoked with an option's `value` when that option has no own `handler`. */
  onSelect?: (value: string) => void;
  align?: 'start' | 'end';
  matchWidth?: boolean;
  compact?: boolean;
  className?: string;
  /** Checkbox rows that toggle without closing the dropdown — pair with onMultiToggle. */
  multiSelect?: boolean;
  onMultiToggle?: (value: string) => void;
  /** Cancel/Save footer row — shown when provided (typically paired with multiSelect). */
  onSave?: () => void;
  cancelLabel?: string;
  saveLabel?: string;
}

const PortalDropdown: React.FC<Props> = ({
  isOpen,
  anchorRef,
  portalRef,
  onClose,
  options,
  title,
  onSelect,
  align = 'start',
  matchWidth = true,
  compact = false,
  className,
  multiSelect = false,
  onMultiToggle,
  onSave,
  cancelLabel = 'Cancel',
  saveLabel = 'Save',
}) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, align, matchWidth);

  // portalRef's target renders alongside this component, so its .current is
  // only reliably non-null after this component itself has committed at
  // least once — track that in state instead of reading .current directly
  // during render.
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <>
      {/* portalRef's own root is pointer-events:none so it doesn't block the
          page while nothing is open; this backdrop re-enables pointer-events
          for the full screen only while a dropdown IS open, so an outside
          tap is caught and closes the menu right here instead of passing
          through to (and activating) whatever is underneath. */}
      <div className={styles.dropdownBackdrop} onClick={onClose} />
      <IonCard
        ref={cardRef}
        className={`${styles.customDropdownList}${compact ? ` ${styles.compact}` : ''}${className ? ` ${className}` : ''}`}
        style={{ ...rect, position: 'absolute' }}
      >
        <IonList lines="none" className={styles.customDropdownListInner}>
          {title && (
            <IonItem lines="none" className={styles.titleRow}>
              <IonLabel
                className={styles.titleLabel}
                style={{ color: 'var(--glass-card-text-secondary, var(--ion-color-medium))' } as React.CSSProperties}
              >
                {title}
              </IonLabel>
            </IonItem>
          )}
          {options.map((opt, i) => (
            <IonItem
              key={opt.value ?? i}
              button
              lines="none"
              detail={false}
              className={`${styles.customDropdownItem}${opt.role === 'destructive' ? ` ${styles.customDropdownItemDestructive}` : ''}${opt.selected ? ` ${styles.customDropdownItemActive}` : ''}`}
              onClick={() => {
                if (opt.handler) opt.handler();
                else if (multiSelect) { if (opt.value !== undefined) onMultiToggle?.(opt.value); }
                else if (opt.value !== undefined) onSelect?.(opt.value);
                // Multi-select rows toggle in place — the dropdown only
                // closes via its own Cancel/Save footer or an outside tap.
                if (!multiSelect) onClose();
              }}
            >
              {typeof opt.icon === 'string' ? (
                <IonIcon
                  icon={opt.icon}
                  slot="start"
                  className={styles.customDropdownItemIcon}
                  color={opt.role === 'destructive' ? 'danger' : opt.color}
                />
              ) : opt.icon ? (
                <span
                  slot="start"
                  className={styles.customStartIcon}
                  style={{ color: opt.role === 'destructive' ? 'var(--ion-color-danger)' : (opt.color ? `var(--ion-color-${opt.color})` : undefined) }}
                >
                  {opt.icon}
                </span>
              ) : null}
              {(opt.icon && opt.sublabel) || (multiSelect && opt.sublabel) ? (
                // Action-menu style (icon + description), also used for
                // multi-select rows regardless of icon: sublabel stacks
                // under the label as a description line, not beside it —
                // matches CardActionsPopover's original .labels layout.
                <IonLabel>
                  <IonText className={styles.customDropdownItemLabelText}>{opt.label}</IonText>
                  <IonText className={styles.customDropdownSublabel}>{opt.sublabel}</IonText>
                </IonLabel>
              ) : (
                <IonLabel>{opt.label}</IonLabel>
              )}
              {!opt.icon && !multiSelect && opt.sublabel && <IonText slot="end" className={styles.customDropdownSublabelEnd}>{opt.sublabel}</IonText>}
              {opt.endIcon && <IonIcon icon={opt.endIcon} slot="end" color="primary" />}
              {multiSelect && (
                <IonCheckbox
                  slot="end"
                  checked={!!opt.selected}
                  onIonChange={() => { if (opt.value !== undefined) onMultiToggle?.(opt.value); }}
                  onClick={e => e.stopPropagation()}
                />
              )}
            </IonItem>
          ))}
        </IonList>
        {onSave && (
          <div className={styles.footer}>
            <button type="button" className={styles.footerCancelBtn} onClick={onClose}>{cancelLabel}</button>
            <button type="button" className={styles.footerSaveBtn} onClick={() => { onSave(); onClose(); }}>{saveLabel}</button>
          </div>
        )}
      </IonCard>
    </>,
    portalNode,
  );
};

export default PortalDropdown;
